/**
 * miniMapProjection.logic.ts
 * Pure domain mathematics for Data Center Rack Mini-Map viewport projection.
 *
 * Implements:
 * 1. Normalized Bounding Box calculation (minX, maxX, minY, maxY) with padding.
 * 2. Origin offset normalization (subtracting minX/minY) so coordinate-offset rooms center neatly.
 * 3. Uniform 1:1 aspect ratio preservation (scale = Math.min(scaleX, scaleY)).
 * 4. Cardinal front-door indicator lines (0°, 90°, 180°, 270°).
 * 5. Viewport centering of rectangular halls.
 * 6. Defensive handling for empty lists, single rack, and missing coordinates.
 */

import { rackLabel } from './rackName.logic.ts';

export interface MiniMapRackInput {
  id: string | number;
  address_confirmed?: boolean;
  x_m?: number | null;
  y_m?: number | null;
  width_m?: number | null;
  depth_m?: number | null;
  row_index?: number | null;
  rack_index?: number | null;
  rotation?: number | null;
  [key: string]: any;
}

export interface MiniMapViewportConfig {
  width: number;
  height: number;
  padding: number;
}

export interface ProjectedDoorIndicator {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export interface ProjectedRack {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  isCurrent: boolean;
  door: ProjectedDoorIndicator;
  raw: MiniMapRackInput;
  label: string;
  tooltip: string;
}

export interface MiniMapBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  widthM: number;
  heightM: number;
}

export interface MiniMapProjectionResult {
  hasRealCoords: boolean;
  racks: ProjectedRack[];
  bounds: MiniMapBounds;
  scale: number;
  viewport: {
    width: number;
    height: number;
  };
}

export const DEFAULT_MINIMAP_VIEWPORT: MiniMapViewportConfig = {
  width: 200,
  height: 80,
  padding: 8,
};

function toFiniteNumber(val: any, fallback: number): number {
  if (val == null || val === '') return fallback;
  const n = Number(val);
  return Number.isFinite(n) ? n : fallback;
}

function toNullableFiniteNumber(val: any): number | null {
  if (val == null || val === '') return null;
  const n = Number(val);
  return Number.isFinite(n) ? n : null;
}

/**
 * Normalizes rotation angle to cardinal 0, 90, 180, or 270 degrees.
 */
export function normalizeCardinalRotation(deg?: number | string | null): 0 | 90 | 180 | 270 {
  if (deg == null || deg === '') return 0;
  const n = Number(deg);
  if (!Number.isFinite(n)) return 0;
  const norm = ((Math.round(n) % 360) + 360) % 360;
  if (norm >= 45 && norm < 135) return 90;
  if (norm >= 135 && norm < 225) return 180;
  if (norm >= 225 && norm < 315) return 270;
  return 0;
}

/**
 * Formats rack count with proper Russian grammatical pluralization:
 * 1 стойка, 2-4 стойки, 5-20 стоек, 21 стойка, etc.
 */
export function formatRackCount(count: number): string {
  const abs = Math.abs(Math.round(count));
  const mod10 = abs % 10;
  const mod100 = abs % 100;
  if (mod100 >= 11 && mod100 <= 19) return `${abs} стоек`;
  if (mod10 === 1) return `${abs} стойка`;
  if (mod10 >= 2 && mod10 <= 4) return `${abs} стойки`;
  return `${abs} стоек`;
}

/**
 * Projects a collection of racks into normalized 2D SVG viewport coordinates.
 */
export function projectMiniMapLayout(
  racks: MiniMapRackInput[] = [],
  currentRackId?: string | number | null,
  viewportConfig?: Partial<MiniMapViewportConfig>
): MiniMapProjectionResult {
  const vp: MiniMapViewportConfig = {
    width: viewportConfig?.width ?? DEFAULT_MINIMAP_VIEWPORT.width,
    height: viewportConfig?.height ?? DEFAULT_MINIMAP_VIEWPORT.height,
    padding: Math.max(0, viewportConfig?.padding ?? DEFAULT_MINIMAP_VIEWPORT.padding),
  };

  const emptyResult: MiniMapProjectionResult = {
    hasRealCoords: false,
    racks: [],
    bounds: { minX: 0, maxX: 0, minY: 0, maxY: 0, widthM: 0, heightM: 0 },
    scale: 1,
    viewport: { width: vp.width, height: vp.height },
  };

  if (!Array.isArray(racks) || racks.length === 0) {
    return emptyResult;
  }

  // Defensively filter out null, undefined, or non-object items
  const validRacks = racks.filter((r): r is MiniMapRackInput => r != null && typeof r === 'object');
  if (validRacks.length === 0) {
    return emptyResult;
  }

  const currIdStr = currentRackId != null && currentRackId !== '' ? String(currentRackId).trim() : null;

  // Check if real coordinates (x_m, y_m) are available
  const hasRealCoords = validRacks.some(r => {
    const xm = toNullableFiniteNumber(r.x_m);
    const ym = toNullableFiniteNumber(r.y_m);
    return xm !== null && ym !== null;
  });

  // Compute metric coordinates and physical bounding box for each rack
  const normalizedInputs = validRacks.map((r, idx) => {
    const rot = normalizeCardinalRotation(r.rotation);
    const rawW = Math.max(toFiniteNumber(r.width_m, 0.6), 0.1);
    const rawD = Math.max(toFiniteNumber(r.depth_m, 1.0), 0.1);

    // At 90 or 270 deg, rack orientation swaps footprint width and depth
    const isSideways = rot === 90 || rot === 270;
    const effW = isSideways ? rawD : rawW;
    const effH = isSideways ? rawW : rawD;

    let xm: number;
    let ym: number;

    if (hasRealCoords) {
      xm = toFiniteNumber(r.x_m, 0);
      ym = toFiniteNumber(r.y_m, 0);
    } else {
      // Fallback synthetic grid based on row/rack index
      const col = toFiniteNumber(r.rack_index, idx);
      const row = toFiniteNumber(r.row_index, 0);
      xm = col * 1.5;
      ym = row * 2.0;
    }

    const resolvedId = String(r.id != null && r.id !== '' ? r.id : idx).trim();
    const isCurrent = currIdStr !== null && resolvedId === currIdStr;
    const label = r.address_confirmed === false
      ? rackLabel({ label: r.label, address_confirmed: false, row_index: r.row_index ?? 0, rack_index: r.rack_index ?? idx })
      : String(r.label || r.name || `Р${toFiniteNumber(r.row_index, 0) + 1}-C${toFiniteNumber(r.rack_index, idx) + 1}`).trim();
    const unconfirmed = r.address_confirmed === false && !r.is_facility && r.type !== 'pillar' && r.type !== 'column';
    const tooltip = `${label}${unconfirmed ? ' · место не подтверждено' : ''} (ID: ${resolvedId})${isCurrent ? '' : ' — Нажмите для перехода'}`;

    return {
      r,
      id: resolvedId,
      xm,
      ym,
      effW,
      effH,
      rot,
      isCurrent,
      label,
      tooltip,
    };
  });

  // Calculate room extent in a single pass to avoid RangeError stack overflow on large sets
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  for (const n of normalizedInputs) {
    if (n.xm < minX) minX = n.xm;
    if (n.xm + n.effW > maxX) maxX = n.xm + n.effW;
    if (n.ym < minY) minY = n.ym;
    if (n.ym + n.effH > maxY) maxY = n.ym + n.effH;
  }

  // Guard against non-finite edge cases
  if (!Number.isFinite(minX)) minX = 0;
  if (!Number.isFinite(maxX)) maxX = 0.6;
  if (!Number.isFinite(minY)) minY = 0;
  if (!Number.isFinite(maxY)) maxY = 1.0;

  const roomW = Math.max(maxX - minX, 0.001);
  const roomH = Math.max(maxY - minY, 0.001);

  const availW = Math.max(1, vp.width - vp.padding * 2);
  const availH = Math.max(1, vp.height - vp.padding * 2);

  // Uniform 1:1 scale
  let scale: number;
  if (normalizedInputs.length === 1 || (roomW <= 0.01 && roomH <= 0.01)) {
    // Single rack: pick visual size that fits comfortably within viewport without overflow
    const maxRackW = normalizedInputs[0]?.effW ?? 0.6;
    const maxRackH = normalizedInputs[0]?.effH ?? 1.0;
    scale = Math.min(availW / (maxRackW * 2.5), availH / (maxRackH * 2.5), availH / 3.0);
  } else {
    scale = Math.min(availW / roomW, availH / roomH);
  }

  // Centering offsets
  const occupiedW = roomW * scale;
  const occupiedH = roomH * scale;
  const offsetX = vp.padding + (availW - occupiedW) / 2;
  const offsetY = vp.padding + (availH - occupiedH) / 2;

  // Project each rack
  const projectedRacks: ProjectedRack[] = normalizedInputs.map(item => {
    const rx = offsetX + (item.xm - minX) * scale;
    const ry = offsetY + (item.ym - minY) * scale;
    const rw = Math.max(item.effW * scale, 3);
    const rh = Math.max(item.effH * scale, 2);

    // Calculate front-door accent line
    // 0 deg: Front is South (+Y) -> bottom edge
    // 180 deg: Front is North (-Y) -> top edge
    // 90 deg: Front is West (-X) -> left edge
    // 270 deg: Front is East (+X) -> right edge
    let door: ProjectedDoorIndicator;
    switch (item.rot) {
      case 180: // North
        door = { x1: rx, y1: ry, x2: rx + rw, y2: ry };
        break;
      case 90: // West
        door = { x1: rx, y1: ry, x2: rx, y2: ry + rh };
        break;
      case 270: // East
        door = { x1: rx + rw, y1: ry, x2: rx + rw, y2: ry + rh };
        break;
      case 0: // South (default)
      default:
        door = { x1: rx, y1: ry + rh, x2: rx + rw, y2: ry + rh };
        break;
    }

    return {
      id: item.id,
      x: rx,
      y: ry,
      width: rw,
      height: rh,
      isCurrent: item.isCurrent,
      door,
      raw: item.r,
      label: item.label,
      tooltip: item.tooltip,
    };
  });

  return {
    hasRealCoords,
    racks: projectedRacks,
    bounds: {
      minX,
      maxX,
      minY,
      maxY,
      widthM: roomW,
      heightM: roomH,
    },
    scale,
    viewport: {
      width: vp.width,
      height: vp.height,
    },
  };
}
